import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import ProgressionTab from '../../../../../theme/ProgressionBuilder/ProgressionTab';
import Text from '../../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import EtailizeFieldRenderer from '../../components/etailize/EtailizeFieldRenderer';
import EtailizeErrorBoundary from '../../components/etailize/EtailizeErrorBoundary';
import { validateField } from '../../utils/etailize/etailizeValidation';
import {  logError } from '../../utils/etailize/etailizeErrorHandling';
import { processFieldConfigurations } from '../../utils/etailize/etailizeHelpers';

/**
 * EtailizeAuthentication Component
 * 
 * Handles dynamic authentication field rendering for selected Etailize marketplace.
 * This component will render authentication fields based on the marketplace's
 * connection definition requirements.
 * 
 * Props:
 * - handleDataUpdate: Function to update progression state
 * - handleNext: Function to navigate to next step
 * - handlePrevious: Function to navigate to previous step
 * - data: Current progression data containing selected marketplace
 */
const Content = (props) => {
    const { t } = useTranslation();
    const [authData, setAuthData] = useState({});
    const [isValidating, setIsValidating] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [fieldValidationStatus, setFieldValidationStatus] = useState({});
    const [connectionError, setConnectionError] = useState(null);
    const [networkError, setNetworkError] = useState(null);

    // Get selected marketplace from progression data
    const selectedMarketplace = props.data?.etailize_marketplace;

    // Debounce utility function
    const useDebounce = (callback, delay) => {
        const [debounceTimer, setDebounceTimer] = useState(null);

        const debouncedCallback = useCallback((...args) => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            
            const timer = setTimeout(() => {
                callback(...args);
            }, delay);
            
            setDebounceTimer(timer);
        }, [callback, delay, debounceTimer]);

        return debouncedCallback;
    };

    useEffect(() => {
        // Debug logging to track field initialization
        console.log('EtailizeAuthentication: Initializing auth data for marketplace:', {
            marketplaceName: selectedMarketplace?.name,
            hasFields: !!selectedMarketplace?.fields,
            hasRequiredFields: !!selectedMarketplace?.required_fields,
            fieldsLength: selectedMarketplace?.fields?.length || 0,
            requiredFieldsLength: selectedMarketplace?.required_fields?.length || 0,
            marketplace: selectedMarketplace
        });

        // Initialize auth data based on marketplace field definitions
        if (selectedMarketplace?.fields) {
            const initialAuthData = {};
            selectedMarketplace.fields.forEach((field, index) => {
                console.log(`Processing field ${index}:`, {
                    field: field,
                    fieldName: field?.name,
                    fieldId: field?.id,
                    fieldType: field?.type,
                    fieldDefaultValue: field?.defaultValue,
                    fieldDefault: field?.default_value
                });

                // Enhanced field initialization with null/undefined checks
                if (field && (field.name || field.id)) {
                    const fieldName = field.name || field.id;
                    const defaultValue = field.defaultValue || field.default_value || '';
                    
                    console.log(`Initializing field "${fieldName}" with value:`, defaultValue);
                    initialAuthData[fieldName] = defaultValue;
                } else {
                    console.warn('Skipping invalid field:', field);
                }
            });
            
            console.log('Final initialized auth data:', initialAuthData);
            setAuthData(initialAuthData);
        } else if (selectedMarketplace?.required_fields) {
            // Fallback for simple required_fields array
            const initialAuthData = {};
            selectedMarketplace.required_fields.forEach((fieldName, index) => {
                console.log(`Processing required field ${index}:`, {
                    fieldName: fieldName,
                    type: typeof fieldName,
                    valid: fieldName && typeof fieldName === 'string' && fieldName.trim() !== ''
                });

                // Only initialize if fieldName is valid
                if (fieldName && typeof fieldName === 'string' && fieldName.trim() !== '') {
                    initialAuthData[fieldName] = '';
                } else {
                    console.warn('Skipping invalid required field:', fieldName);
                }
            });
            
            console.log('Final initialized auth data (required fields):', initialAuthData);
            setAuthData(initialAuthData);
        } else {
            console.log('No fields or required_fields found, setting empty auth data');
            setAuthData({});
        }
    }, [selectedMarketplace]);

    // Cleanup debounce timers on unmount
    useEffect(() => {
        return () => {
            // Clear any pending timers when component unmounts
            if (window.etailizeDebounceTimer) {
                clearTimeout(window.etailizeDebounceTimer);
            }
        };
    }, []);

    // Get field definitions function
    const getFieldDefinitions = useCallback(() => {
        try {
            if (selectedMarketplace?.fields) {
                // Use helper function to ensure proper field processing
                const processedFields = processFieldConfigurations(selectedMarketplace);
                return Array.isArray(processedFields) ? processedFields : [];
            } else if (selectedMarketplace?.required_fields) {
                // Legacy format - create field definitions manually
                return selectedMarketplace.required_fields.map(fieldName => ({
                    name: fieldName,
                    type: 'Text',
                    required: true,
                    label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                }));
            }
        } catch (error) {
            console.warn('Error processing field definitions:', error);
        }
        return [];
    }, [selectedMarketplace]);

    // Debounced validation function
    const performValidation = useCallback((fieldName, value) => {
        const fields = getFieldDefinitions();
        const field = fields.find(f => f.name === fieldName);
        
        if (field) {
            // Validate the field
            const error = validateField(field, value, t);
            
            console.log('Debounced validation result:', {
                fieldName: fieldName,
                value: value,
                error: error,
                field: field
            });
            
            // Update validation errors
            setValidationErrors(prev => ({
                ...prev,
                [fieldName]: error || undefined
            }));
            
            // Update field validation status
            setFieldValidationStatus(prev => ({
                ...prev,
                [fieldName]: error ? 'invalid' : 'valid'
            }));
        }
    }, [t, getFieldDefinitions]);

    // Create debounced validation with 300ms delay
    const debouncedValidation = useDebounce(performValidation, 300);

    const handleFieldChange = (fieldName, value) => {
        // Check if this is an OAuth response with connection fields
        if (typeof value === 'object' && value !== null && value.Secret) {
            console.log('EtailizeAuthentication handling OAuth connection fields:', value);
            
            // Update auth data with all connection fields
            const updatedAuthData = {
                ...authData,
                ...value
            };
            
            setAuthData(updatedAuthData);
            
            // Update progression data with connection fields
            props.handleDataUpdate({
                etailize_auth_data: updatedAuthData,
                etailize_connection_fields: value
            });
            
            return;
        }
        
        // Handle regular field changes
        const sanitizedValue = value === undefined || value === null ? '' : value;
        
        console.log('EtailizeAuthentication handleFieldChange:', {
            fieldName: fieldName,
            originalValue: value,
            sanitizedValue: sanitizedValue,
            typeOfValue: typeof value,
            isUndefined: value === undefined,
            isNull: value === null
        });

        // Update the field value immediately
        setAuthData(prev => ({
            ...prev,
            [fieldName]: sanitizedValue
        }));

        // Update progression data immediately
        props.handleDataUpdate({
            etailize_auth_data: {...authData, [fieldName]: sanitizedValue}
        });
        
        // Set field status to pending immediately
        setFieldValidationStatus(prev => ({
            ...prev,
            [fieldName]: 'pending'
        }));
        
        // Perform debounced validation
        debouncedValidation(fieldName, sanitizedValue);
    };

    const clearFieldError = (fieldName) => {
        if (validationErrors[fieldName]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    // If no marketplace is selected, show error state
    if (!selectedMarketplace) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Text color="error" fontSize={18} fontWeight="bold" sx={{ mb: 2 }}>
                    {t('no_marketplace_selected')}
                </Text>
                <Text color="textSecondary" sx={{ mb: 3 }}>
                    {t('please_select_marketplace_first')}
                </Text>
                <Text color="textSecondary">
                    {t('go_back_to_select_marketplace')}
                </Text>
            </Box>
        );
    }

    return (
        <Box>
            <Text fontSize={18} fontWeight="bold" sx={{ mb: 1 }}>
                {`${selectedMarketplace.name} ${t("setup")}` }
            </Text>
            
            {/* Error Display */}
            {(connectionError || networkError) && (
                <Box sx={{ mb: 3 }}>
                    {networkError && (
                        <Box sx={{ 
                            p: 2, 
                            mb: 2,
                            backgroundColor: '#ffebee',
                            borderRadius: 2,
                            border: '1px solid #f44336'
                        }}>
                            <Text fontWeight="bold" color="error" sx={{ mb: 1 }}>
                                🌐 {t('network_error')}
                            </Text>
                            <Text fontSize={12} color="error">
                                {networkError.message}
                            </Text>
                        </Box>
                    )}
                    
                    {connectionError && (
                        <Box sx={{ 
                            p: 2, 
                            mb: 2,
                            backgroundColor: '#fff3cd',
                            borderRadius: 2,
                            border: '1px solid #ffc107'
                        }}>
                            <Text fontWeight="bold" color="warning.dark" sx={{ mb: 1 }}>
                                🔗 {t('connection_error')}
                            </Text>
                            <Text fontSize={12} color="warning.dark">
                                {connectionError.message}
                            </Text>
                            {connectionError.details && (
                                <Text fontSize={11} color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    {connectionError.details}
                                </Text>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* Authentication Fields */}
            <EtailizeErrorBoundary
                t={t}
                onError={(error) => logError(error, { component: 'EtailizeAuthentication' })}
            >
                <Box sx={{ mb: 3 }}>
                    <Text fontWeight="medium" sx={{ mb: 2 }}>
                        {t('authentication_fields')}
                    </Text>
                    
                    {(() => {
                        const fields = getFieldDefinitions();
                        
                        if (fields.length > 0) {
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {fields.map(field => {
                                        // Ensure field value is never undefined or null
                                        const fieldValue = authData[field.name];
                                        const sanitizedValue = fieldValue === undefined || fieldValue === null ? '' : fieldValue;
                                        
                                        return (
                                            <EtailizeFieldRenderer
                                                key={field.name}
                                                field={field}
                                                value={sanitizedValue}
                                                onChange={handleFieldChange}
                                                error={validationErrors[field.name] || undefined}
                                                onClearError={clearFieldError}
                                                validationStatus={fieldValidationStatus[field.name]}
                                            />
                                        );
                                    })}
                                </Box>
                            );
                        } else {
                            return (
                                <Box sx={{ 
                                    p: 3, 
                                    border: '1px solid #e0e0e0', 
                                    borderRadius: 2,
                                    textAlign: 'center'
                                }}>
                                    <Text color="textSecondary">
                                        {t('no_authentication_required')}
                                    </Text>
                                </Box>
                            );
                        }
                    })()}
                </Box>
            </EtailizeErrorBoundary>

        </Box>
    );
};

const EtailizeAuthentication = (props) => {
    const { t } = useTranslation();

    return (
        <ProgressionTab 
            tabId="tabEtailizeAuthentication" 
            subtitle={t('authentication')}
            step={5} 
            maxSteps={5}
        >
            <Content {...props} />
        </ProgressionTab>
    );
};

export default EtailizeAuthentication;